import { apiService } from './api'
import { WRITING_ENDPOINTS } from '@/api/endpoints'
import type {
  WritingAssistantResponse,
  WritingAssistantDetailResponse,
  WritingAssistantListResponse,
  WritingAssistantUpdateResponse,
  WritingAssistantRegenerateResponse,
  WritingAssistantTranslateResponse,
} from '../types/writing'
import {
  WritingAssistantResponseSchema,
  WritingAssistantDetailResponseSchema,
  WritingAssistantListResponseSchema,
  WritingAssistantUpdateResponseSchema,
  WritingAssistantRegenerateResponseSchema,
  WritingAssistantTranslateResponseSchema,
} from '../types/writing'
import { z } from 'zod'

export interface CreateWritingAssistantData {
  contentType: string
  extraInstructions?: string
  bobAnalyseId?: string
  selectedBoodschappen?: string[]
}

export class WritingAssistantService {
  async createWritingAssistant(
    data: CreateWritingAssistantData
  ): Promise<WritingAssistantResponse> {
    return apiService.post(WRITING_ENDPOINTS.NEW, data, undefined, WritingAssistantResponseSchema)
  }

  async getWritingAssistants(crisisId?: string): Promise<WritingAssistantListResponse> {
    const params = crisisId ? { crisis_id: crisisId } : {}
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${WRITING_ENDPOINTS.LIST}?${queryString}` : WRITING_ENDPOINTS.LIST

    return apiService.get(url, WritingAssistantListResponseSchema)
  }

  async getWritingAssistant(writingId: string): Promise<WritingAssistantDetailResponse> {
    return apiService.get(
      WRITING_ENDPOINTS.WRITING_BY_ID(writingId),
      WritingAssistantDetailResponseSchema
    )
  }

  async updateWritingAssistantContent(
    writingId: string,
    content: string
  ): Promise<WritingAssistantUpdateResponse> {
    return apiService.post(
      WRITING_ENDPOINTS.UPDATE_CONTENT(writingId),
      { content },
      undefined,
      WritingAssistantUpdateResponseSchema
    )
  }

  async regenerateWritingAssistant(writingId: string): Promise<WritingAssistantRegenerateResponse> {
    return apiService.post(
      WRITING_ENDPOINTS.REGENERATE(writingId),
      {},
      undefined,
      WritingAssistantRegenerateResponseSchema
    )
  }

  async adjustWritingAssistantContent(
    writingId: string,
    additionalInstructions: string
  ): Promise<WritingAssistantRegenerateResponse> {
    return apiService.post(
      WRITING_ENDPOINTS.ADJUST_CONTENT(writingId),
      { additional_instructions: additionalInstructions },
      undefined,
      WritingAssistantRegenerateResponseSchema
    )
  }

  async translateWritingAssistant(
    writingId: string,
    targetLanguage: string
  ): Promise<WritingAssistantTranslateResponse> {
    return apiService.post(
      WRITING_ENDPOINTS.TRANSLATE(writingId),
      { target_language: targetLanguage },
      undefined,
      WritingAssistantTranslateResponseSchema
    )
  }

  async deleteWritingAssistant(
    writingId: string
  ): Promise<{ data: { id: string; deleted: boolean } }> {
    const schema = z.object({
      data: z.object({
        id: z.string(),
        deleted: z.boolean(),
      }),
    })

    return apiService.delete(WRITING_ENDPOINTS.DELETE(writingId), schema)
  }
}

export const writingAssistantService = new WritingAssistantService()
